import { ref, watch, computed } from "@vue/composition-api";
import axios from "@/libs/axios";
import store from "@/store";
import { debounce } from 'lodash';

export default function useList() {
  const tableColumns = [
    { key: "actions", label: "actions.name", sortable: false },
    { key: "icon", label: "", sortable: false },
    { key: "reference_number", label: "operational_security.crisis_form.reference_number" },
    { key: "name", label: "operational_security.crisis_form.name" }
  ];

  store.dispatch("unit/getAll", { hasCrisisUnits: true });

  const userOptions = ref([]);
  const endDateError = ref('');
  const startDateError = ref('');

  const unitOptions = computed(() => store.getters["unit/getSelectedItems"]);

  const perPage = ref(10);
  const currentPage = ref(1);
  const totalList = ref(0);
  const searchQuery = ref("");
  const sortBy = ref("created_at");
  const isSortDirDesc = ref(true);
  const filters = ref({
    units: [],
    users: [],
    status: [],
    startDate: null,
    endDate: null
  });

  const filtersCount = computed(() => {
    let count = 0;

    if (filters.value.units.length) count++;
    if (filters.value.users.length) count++;
    if (filters.value.status.length) count++;
    if (filters.value.startDate || filters.value.endDate) count++;

    return count;
  });

  const clearSearchFilters = () => {
    filters.value = {
      units: [],
      users: [],
      status: [],
      startDate: null,
      endDate: null
    };

    currentPage.value = 1;
    refetchData();
  };

  const buildFilters = () => {
    filters.value = {
      units: filters.value.units || [],
      users: filters.value.users || [],
      status: filters.value.status || [],
      startDate: filters.value.startDate || null,
      endDate: filters.value.endDate || null
    };
    currentPage.value = 1;
    refetchData();
  };

  const dataMeta = computed(() => {
    const localItemsCount = refListTable.value ? refListTable.value.localItems.length : 0;
    return {
      from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
      to: perPage.value * (currentPage.value - 1) + localItemsCount,
      of: totalList.value,
    };
  });

  const refListTable = ref(null);
  const perPageOptions = [10, 25, 50, 100];

  const fetchList = (ctx) => new Promise((resolve, reject) => {
    const queryParams = {
      max: perPage.value,
      "page[n]": currentPage.value,
      per_page: perPage.value,
      sort_by: sortBy.value,
      sort_desc: isSortDirDesc.value ? 1 : 0,
      q: searchQuery.value,
      units: filters.value.units,
      users_rh: filters.value.users,
      status: filters.value.status,
      start_date: filters.value.startDate,
      end_date: filters.value.endDate
    };

    axios
      .get("/v1/operational-security-crisis", { params: queryParams })
      .then((response) => {
        const { data, meta } = response.data;
        resolve(data);
        totalList.value = meta.total;
      })
      .catch((error) => reject(error));
  });

  const usersRhList = () => {
    const params = {
      has_crisis: true
    }

    axios.get('v1/users-rh', { params }).then(response => {
      const { data } = response;

      userOptions.value = data.data;
    })
    .catch(() => {
      userOptions.value = []
    });
  }

  const refetchData = () => { refListTable.value.refresh(); };

  watch([currentPage, perPage, sortBy, isSortDirDesc], () => { refetchData(); });
  watch([searchQuery], debounce(() => { refetchData(); }, 500));

  const formatDateAssigned = (value) => {
    if (!value) return "";
    const date = new Date(value);
    return new Intl.DateTimeFormat("default", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    }).format(date);
  };

  const getUserAsigned = (lastStatus) => {
    if (!lastStatus) {
      return '-';
    }

    const { user } = lastStatus;

    return (user) ? `${user.user} - ${user.full_name}` : '-';
  }

  const getUserReview = (penetrationTestingStatus) => {
    const statusReviewTesting = penetrationTestingStatus.find(
      pnts => (pnts.status.name === 'in_review')
    );

    if (!statusReviewTesting) {
      return '-';
    }

    const { user_validation } = statusReviewTesting;

    return (user_validation) ? `${user_validation.user} - ${user_validation.full_name}` : '-';
  }

  const getUserValidator = (lastStatus) => {
    if (!lastStatus) {
      return '-';
    }

    const validationStatuses = ['approved','canceled','rejected'];
    const { status } = lastStatus;

    if (!validationStatuses.includes(status.name)) {
      return '-';
    }

    const { user_validation } = lastStatus;

    return (user_validation) ? `${user_validation.user} - ${user_validation.full_name}` : '-';
  }

  usersRhList();

  return {
    fetchList,
    tableColumns,
    perPage,
    currentPage,
    totalList,
    dataMeta,
    perPageOptions,
    searchQuery,
    sortBy,
    isSortDirDesc,
    refListTable,
    refetchData,
    formatDateAssigned,
    filters,
    filtersCount,
    clearSearchFilters,
    buildFilters,
    unitOptions,
    userOptions,
    getUserAssigned: getUserAsigned,
    getUserReview,
    getUserValidator,
    endDateError,
    startDateError
  };
}


